"use client";

import Image from "next/image";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import GoCaptcha from "go-captcha-react";
import { ArrowRight } from "lucide-react";
import type { SiteFooterCopy } from "@/shared/i18n/site-copy";
import { cn } from "@/shared/utils/cn";

const subscribePath = "/firefly-server/email-subscribe";
const captchaPath = "/firefly-server/email-subscribe/captcha";
const workspaceId = "firefly";
const listIds = ["community"];
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

type SubscribeCopy = SiteFooterCopy["subscribe"];

type ApiResponse<T> = {
  code: number;
  data: T;
  msg: string;
};

type CaptchaResponse = {
  captcha_id: string;
  height: number;
  image: string;
  thumb: string;
  thumb_height: number;
  thumb_width: number;
  width: number;
};

type CaptchaDot = {
  x: number;
  y: number;
};

type SubscribeStatus =
  | "idle"
  | "loading-captcha"
  | "submitting"
  | "success"
  | "error";

type FooterSubscribeFormProps = {
  copy: SubscribeCopy;
  inputId?: string;
  variant?: "desktop" | "mobile";
};

/**
 * 渲染页脚邮件订阅表单并处理验证码与提交。
 * @param props 组件参数。
 * @param props.copy 订阅区域的多语言文案。
 * @param props.inputId 邮箱输入框的唯一 id。
 * @param props.variant 当前表单使用的桌面或移动端视觉规格。
 */
export function FooterSubscribeForm({
  copy,
  inputId = "site-footer-email",
  variant = "desktop",
}: FooterSubscribeFormProps) {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [captcha, setCaptcha] = useState<CaptchaResponse | null>(null);
  const [isCaptchaOpen, setIsCaptchaOpen] = useState(false);
  const [isEmailEmptyError, setIsEmailEmptyError] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<SubscribeStatus>("idle");
  const [toastMessage, setToastMessage] = useState("");
  const isBusy = status === "loading-captcha" || status === "submitting";

  useEffect(() => {
    if (!toastMessage) {
      return;
    }

    const timer = window.setTimeout(() => {
      setToastMessage("");
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [toastMessage]);

  const captchaConfig = useMemo(
    () => ({
      buttonText: copy.buttonLabel,
      height: captcha?.height ?? 160,
      thumbHeight: captcha?.thumb_height ?? 40,
      thumbWidth: captcha?.thumb_width ?? 160,
      title: copy.captchaTitle,
      width: captcha?.width ?? 320,
    }),
    [captcha, copy.buttonLabel, copy.captchaTitle],
  );

  const captchaData = useMemo(
    () => ({
      image: captcha?.image ?? "",
      thumb: captcha?.thumb ?? "",
    }),
    [captcha],
  );

  const loadCaptcha = useCallback(async () => {
    setStatus("loading-captcha");
    setMessage("");

    try {
      const payload = await requestFireflyJson<CaptchaResponse>(captchaPath, {
        method: "GET",
      });
      setCaptcha(payload);
      setIsCaptchaOpen(true);
      setStatus("idle");
    } catch {
      setCaptcha(null);
      setIsCaptchaOpen(false);
      setMessage(copy.errorMessage);
      setStatus("error");
    }
  }, [copy.errorMessage]);

  const submitSubscription = useCallback(
    async (dots: CaptchaDot[], resetCaptcha: () => void) => {
      if (!captcha) {
        setMessage(copy.captchaErrorMessage);
        setStatus("error");
        return;
      }

      setStatus("submitting");
      setMessage("");

      try {
        await requestFireflyJson<Record<string, never>>(subscribePath, {
          body: JSON.stringify({
            captcha_data: {
              dots: dots.map((dot) => ({
                x: Math.round(dot.x),
                y: Math.round(dot.y),
              })),
            },
            captcha_id: captcha.captcha_id,
            email,
            hp: honeypot,
            list_ids: listIds,
            workspace_id: workspaceId,
          }),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        });

        setEmail("");
        setHoneypot("");
        setCaptcha(null);
        setIsCaptchaOpen(false);
        setMessage("");
        setToastMessage(copy.successMessage);
        setStatus("success");
      } catch (error) {
        resetCaptcha();
        setMessage(getErrorMessage(error) || copy.errorMessage);
        setStatus("error");
        void loadCaptcha();
      }
    },
    [
      captcha,
      copy.captchaErrorMessage,
      copy.errorMessage,
      copy.successMessage,
      email,
      honeypot,
      loadCaptcha,
    ],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) {
        setIsEmailEmptyError(true);
        setMessage("");
        setStatus("error");
        return;
      }
      if (!emailPattern.test(normalizedEmail)) {
        setIsEmailEmptyError(false);
        setMessage(copy.invalidEmailMessage);
        setStatus("error");
        return;
      }

      setIsEmailEmptyError(false);
      setEmail(normalizedEmail);
      void loadCaptcha();
    },
    [copy.invalidEmailMessage, email, loadCaptcha],
  );

  return (
    <div className={cn("relative", variant === "mobile" ? "mt-5" : "mt-4")}>
      {toastMessage ? (
        <div
          className="fixed left-1/2 top-5 z-50 -translate-x-1/2 rounded-[5px] border border-[#b9dfc5] bg-white px-4 py-2 text-sm text-[#1f7a3f] shadow-lg shadow-black/10"
          role="status"
        >
          {toastMessage}
        </div>
      ) : null}
      <form
        className={cn(
          "flex overflow-hidden rounded-[5px] border",
          variant === "mobile" ? "h-[50px]" : "h-10",
          isEmailEmptyError ? "border-[#cc3d00]" : "border-[#cccccc]",
        )}
        onSubmit={handleSubmit}
      >
        <label className="sr-only" htmlFor={inputId}>
          {copy.placeholder}
        </label>
        <input
          id={inputId}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-sm text-black outline-none placeholder:text-[#cccccc]",
            variant === "mobile" ? "px-[15px]" : "px-3",
          )}
          disabled={isBusy}
          aria-invalid={isEmailEmptyError}
          onChange={(event) => {
            setEmail(event.target.value);
            if (isEmailEmptyError) {
              setIsEmailEmptyError(false);
            }
          }}
          placeholder={copy.placeholder}
          type="email"
          value={email}
        />
        <input
          aria-hidden="true"
          autoComplete="off"
          className="pointer-events-none absolute -left-[9999px] top-auto size-px opacity-0"
          onChange={(event) => setHoneypot(event.target.value)}
          tabIndex={-1}
          type="text"
          value={honeypot}
        />
        <input name="workspace_id" type="hidden" value={workspaceId} />
        <input name="list_ids[]" type="hidden" value={listIds[0]} />
        <button
          type="submit"
          aria-label={copy.buttonLabel}
          className={cn(
            "flex shrink-0 cursor-pointer items-center justify-center text-white transition disabled:cursor-not-allowed",
            variant === "mobile"
              ? "size-[50px] bg-transparent disabled:opacity-50"
              : "size-10 bg-[#ff6600] hover:bg-[#e85c00] disabled:bg-[#cccccc]",
          )}
          disabled={isBusy}
        >
          {variant === "mobile" ? (
            <Image
              alt=""
              aria-hidden="true"
              className="size-[50px]"
              height={50}
              src="/figma-assets/footer-subscribe-submit.svg"
              unoptimized
              width={50}
            />
          ) : (
            <ArrowRight className="size-4" />
          )}
        </button>
      </form>

      {/* {message || status === "submitting" ? (
        <p
          className={`mt-2 text-xs ${status === "success" ? "text-[#1f7a3f]" : "text-[#cc3d00]"}`}
          role={status === "error" ? "alert" : "status"}
        >
          {status === "submitting" ? copy.submittingLabel : message}
        </p>
      ) : null} */}

      {isCaptchaOpen && captcha ? (
        <div className="absolute right-0 top-full z-30 mt-3 w-[min(320px,calc(100vw-2rem))] rounded-[5px] border border-[#dddddd] bg-white p-3 shadow-lg shadow-black/10">
          <GoCaptcha.Click
            config={captchaConfig}
            data={captchaData}
            events={{
              close: () => setIsCaptchaOpen(false),
              confirm: (dots: CaptchaDot[], reset: () => void) => {
                void submitSubscription(dots, reset);
              },
              refresh: () => {
                void loadCaptcha();
              },
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * 请求 Firefly 后端统一响应接口。
 * @param path 接口路径。
 * @param init fetch 请求配置。
 * @returns 接口 data 字段。
 */
async function requestFireflyJson<T>(path: string, init: RequestInit) {
  const response = await fetch(buildFireflyApiUrl(path), {
    cache: "no-store",
    ...init,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = (await response.json()) as Partial<ApiResponse<T>>;
  if (payload.code !== 0) {
    throw new Error(payload.msg || "请求失败");
  }

  return payload.data as T;
}

/**
 * 构造客户端可请求的 Firefly API 地址。
 * @param path 接口路径。
 * @returns 完整或相对接口地址。
 */
function buildFireflyApiUrl(path: string) {
  const baseUrl = process.env.FIREFLY_API_BASE_URL?.replace(
    /\/+$/u,
    "",
  );
  return baseUrl ? `${baseUrl}${path}` : path;
}

/**
 * 提取错误提示文案。
 * @param error 原始异常。
 * @returns 可展示的错误信息。
 */
function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "";
}
